package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.key
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarIconButton
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import org.koin.compose.koinInject

@Composable
fun GestanteComorbiditiesScreen(
    patientId: Long,
    onBack: () -> Unit,
    viewModel: GestanteDetailViewModel = koinInject()
) {
    val state by viewModel.state.collectAsState()
    LaunchedEffect(patientId) { viewModel.load(patientId) }
    key(patientId) {
        GestanteComorbiditiesContent(
            state = state, onBack = onBack, onRetry = { viewModel.load(patientId) },
            onAdd = { description, observation -> viewModel.addComorbidity(patientId, description, observation) },
            onUpdate = { id, observation -> viewModel.updateComorbidity(patientId, id, observation) },
            onDeactivate = { id -> viewModel.deactivateComorbidity(patientId, id) },
            onClearFeedback = { viewModel.clearActionFeedback() }
        )
    }
}

@Composable
internal fun GestanteComorbiditiesContent(
    state: GestanteDetailUiState, onBack: () -> Unit, onRetry: () -> Unit,
    onAdd: (String, String?) -> Unit, onUpdate: (Long, String?) -> Unit,
    onDeactivate: (Long) -> Unit, onClearFeedback: () -> Unit
) {
    var showingAddForm by rememberSaveable { mutableStateOf(false) }
    var editing by rememberSaveable { mutableStateOf(false) }
    val content = state as? GestanteDetailUiState.Content
    ClinicalSubscreenScaffold(
        title = "Comorbidades",
        onBack = onBack
    ) { modifier ->
        when (val current = state) {
            GestanteDetailUiState.Loading -> ClinicalSubscreenLoading(modifier)
            is GestanteDetailUiState.Error -> ClinicalSubscreenError(
                message = current.message,
                modifier = modifier,
                onRetry = onRetry
            )
            is GestanteDetailUiState.Content -> {
                ClinicalSubscreenContent(modifier, showingAddForm || editing) {
                    val activeCount = current.detail.comorbidities.count { it.active }
                    ClinicalScreenOverview(
                        icon = PartejarIcons.Navigation.prontuario,
                        headline = if (activeCount == 1) "1 condição ativa" else "$activeCount condições ativas",
                        description = "Acompanhe as condições clínicas ativas desta gestante."
                    )
                    ComorbidityManagementContent(
                        comorbidities = current.detail.comorbidities,
                        saving = current.saving,
                        actionRevision = current.actionRevision,
                        actionError = current.actionError,
                        showingAddForm = showingAddForm,
                        onShowingAddFormChange = { onClearFeedback(); showingAddForm = it },
                        onEditingChange = { onClearFeedback(); editing = it },
                        onAdd = onAdd,
                        onUpdate = onUpdate,
                        onDeactivate = onDeactivate
                    )
                }
            }
        }
    }
}

@Composable
private fun ClinicalSubscreenScaffold(
    title: String,
    onBack: () -> Unit,
    content: @Composable (Modifier) -> Unit
) {
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = title,
                leadingContent = {
                    PartejarIconButton(onClick = onBack) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                }
            )
        }
    ) { padding ->
        content(Modifier.fillMaxSize().padding(padding).imePadding())
    }
}

@Composable
private fun ClinicalSubscreenContent(
    modifier: Modifier,
    showingForm: Boolean,
    content: @Composable () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val scroll = rememberScrollState()
    LaunchedEffect(showingForm) { scroll.scrollTo(0) }
    Column(
        modifier = modifier
            .verticalScroll(scroll)
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
            .padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space20)
    ) {
        content()
    }
}

@Composable
private fun ClinicalScreenOverview(
    icon: ImageVector,
    headline: String,
    description: String
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(spacing.space16),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            modifier = Modifier.size(dimensions.minimumTouchTarget),
            shape = MaterialTheme.shapes.extraLarge,
            color = MaterialTheme.colorScheme.primaryContainer,
            contentColor = MaterialTheme.colorScheme.onPrimaryContainer
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(icon, contentDescription = null, modifier = Modifier.size(dimensions.iconMedium))
            }
        }
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(spacing.space4)
        ) {
            Text(headline, style = MaterialTheme.typography.headlineSmall)
            Text(
                text = description,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun ClinicalSubscreenLoading(modifier: Modifier) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = modifier.padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space20)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space16),
            verticalAlignment = Alignment.CenterVertically
        ) {
            PartejarSkeleton(Modifier.size(PartejarTheme.dimensions.minimumTouchTarget))
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space8)
            ) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.58f).height(spacing.space20))
                PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12))
            }
        }
        repeat(3) {
            PartejarCard(Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(spacing.space12)
                    ) {
                        PartejarSkeletonText(Modifier.weight(1f).height(spacing.space20))
                        PartejarSkeletonText(Modifier.fillMaxWidth(0.24f).height(spacing.space20))
                    }
                    PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.7f).height(spacing.space12))
                }
            }
        }
    }
}

@Composable
private fun ClinicalSubscreenError(
    message: String,
    modifier: Modifier,
    onRetry: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = modifier.padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space16),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        PartejarAlert("Não foi possível carregar", PartejarAlertTone.ERROR) { Text(message) }
        PartejarButton("Tentar novamente", onRetry, motionEnabled = true)
    }
}
